import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Button,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { Invoice, InvoiceStatus, Bid, BidStatus, TransactionWithRelations as Transaction } from '../../types/models';

const BuyerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [availableInvoices, setAvailableInvoices] = useState<Invoice[]>([]);
  const [myBids, setMyBids] = useState<Bid[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch available invoices in marketplace
        const invoicesData = await apiService.get<Invoice[]>('/invoices/marketplace');
        setAvailableInvoices(invoicesData);

        // Fetch buyer's bids - using the standard endpoint which filters by current user
        const bidsData = await apiService.get<Bid[]>('/bids');
        setMyBids(bidsData);

        // Fetch buyer's transactions - using the standard endpoint which filters by current user
        const transactionsData = await apiService.get<Transaction[]>('/transactions');
        setTransactions(transactionsData);
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail 
          ? typeof err.response.data.detail === 'string' 
            ? err.response.data.detail 
            : JSON.stringify(err.response.data.detail)
          : 'Error al cargar los datos del panel';
        
        setError(errorMessage);
        console.error('Dashboard data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.VERIFIED:
        return 'success';
      default:
        return 'default';
    }
  };

  const getBidStatusColor = (status: BidStatus) => {
    switch (status) {
      case BidStatus.ACTIVE:
        return 'info';
      case BidStatus.ACCEPTED:
        return 'success';
      case BidStatus.REJECTED:
        return 'error';
      case BidStatus.EXPIRED:
        return 'warning';
      default:
        return 'default';
    }
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">Panel del Comprador</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<SearchIcon />}
          onClick={() => navigate('/marketplace')}
        >
          Explorar Mercado
        </Button>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#ffebee' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Facturas Disponibles</Typography>
              <Typography variant="h3">{availableInvoices.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Ofertas Activas</Typography>
              <Typography variant="h3">
                {myBids.filter(b => b.status === BidStatus.ACTIVE).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Transacciones Completadas</Typography>
              <Typography variant="h3">{transactions.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Destacados del Mercado</Typography>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Actualizar
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Factura #</TableCell>
                <TableCell>Fecha de Vencimiento</TableCell>
                <TableCell>Monto Neto</TableCell>
                <TableCell>Días al Vencimiento</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" py={2}>
                      No hay facturas disponibles en el mercado en este momento.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                availableInvoices
                  .slice(0, 5)
                  .map((invoice) => {
                    const dueDate = new Date(invoice.due_date);
                    const today = new Date();
                    const daysToMaturity = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                    
                    return (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          {invoice.issuer ? invoice.issuer.name : 'Desconocido'}
                        </TableCell>
                        <TableCell>{invoice.invoice_number}</TableCell>
                        <TableCell>{formatDate(invoice.due_date)}</TableCell>
                        <TableCell>{formatCurrency(invoice.net_amount, invoice.currency)}</TableCell>
                        <TableCell>{daysToMaturity} días</TableCell>
                        <TableCell>
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={() => navigate(`/marketplace/${invoice.id}`)}
                          >
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button onClick={() => navigate('/marketplace')}>Ver Todas las Facturas</Button>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box mb={4}>
        <Typography variant="h5" mb={2}>Mis Ofertas Recientes</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Factura #</TableCell>
                <TableCell>Monto de Oferta</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {myBids.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" py={2}>
                      No has hecho ninguna oferta aún. Explora el mercado para encontrar facturas.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                myBids
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .slice(0, 5)
                  .map((bid) => (
                    <TableRow key={bid.id}>
                      <TableCell>{bid.invoice_id}</TableCell>
                      <TableCell>{formatCurrency(bid.amount)}</TableCell>
                      <TableCell>{formatDate(bid.created_at)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={bid.status} 
                          color={getBidStatusColor(bid.status)} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={bid.status === 'accepted' ? 'Ver detalles de oferta aceptada' : 'Ver detalles de oferta'}>
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={() => navigate(`/bids/${bid.id}`)}
                          >
                            Ver
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button onClick={() => navigate('/bids')}>Ver Todas las Ofertas</Button>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h5" mb={2}>Transacciones Recientes</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Factura #</TableCell>
                <TableCell>Monto de Transacción</TableCell>
                <TableCell>Monto Neto de Factura</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" py={2}>
                      No se han completado transacciones aún. Cuando un vendedor acepte tu oferta, aparecerá aquí.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions
                  .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime())
                  .slice(0, 5)
                  .map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>{transaction.invoice?.invoice_number || transaction.invoice_id}</TableCell>
                      <TableCell>{formatCurrency(transaction.amount)}</TableCell>
                      <TableCell>
                        {transaction.invoice 
                          ? formatCurrency(transaction.invoice.net_amount, transaction.invoice.currency) 
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{formatDate(transaction.completed_at)}</TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => navigate(`/transactions/${transaction.id}`)}
                        >
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button onClick={() => navigate('/transactions')}>Ver Todas las Transacciones</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BuyerDashboard;
